import React, { useEffect, useRef, useState } from "react";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import Button from "./../../../components/Button/Button";
import { QRCodeCanvas } from "qrcode.react";
import {
  FaDownload,
  FaExternalLinkAlt,
  FaFilePdf,
  FaSpinner,
} from "react-icons/fa";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

import { getSettings } from "../../../services/settingsServices";
import useMemberSignupPosterPdf from "./hooks/useMemberSignupPosterPdf";
import { getRegisterUrl } from "./utils/memberSignupPosterUtils";

export default function AideConseilsAdmin() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const docUrl = process.env.REACT_APP_ASSO_DOCUMENTS_URL || "";
  const envSiteUrl = process.env.REACT_APP_URL || "";

  const qrRef = useRef(null);

  const [clubSettings, setClubSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const siteUrl = clubSettings?.siteweb_club || envSiteUrl;
  const registerUrl = getRegisterUrl(clubSettings, siteUrl);

  const { generatePosterPdf, isGeneratingPoster } = useMemberSignupPosterPdf({
  settings: clubSettings,
  registerUrl,
  siteUrl,
});

  useEffect(() => {
    document.title = "Aide & Conseils - Administrateur";
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      try {
        setSettingsLoading(true);

        const response = await getSettings();

        const data = response?.data ?? response;
        const settings = Array.isArray(data) ? data[0] : data;

        if (isMounted) {
          setClubSettings(settings || null);
        }
      } catch (error) {
        console.error("Erreur chargement settings :", error);

        if (isMounted) {
          setClubSettings(null);
        }
      } finally {
        if (isMounted) {
          setSettingsLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleOpenDoc = () => {
    if (!docUrl) return;

    const url = docUrl.includes("/edit")
      ? docUrl.replace("/edit", "/preview")
      : docUrl;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadQR = () => {
    const canvasEl =
      qrRef.current instanceof HTMLCanvasElement
        ? qrRef.current
        : qrRef.current?.querySelector?.("canvas");

    if (!canvasEl) return;

    const scale = 2;
    const newCanvas = document.createElement("canvas");
    newCanvas.width = canvasEl.width * scale;
    newCanvas.height = canvasEl.height * scale;

    const ctx = newCanvas.getContext("2d");
    ctx.scale(scale, scale);
    ctx.drawImage(canvasEl, 0, 0);

    const pngUrl = newCanvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = "qr_site_club.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGeneratePosterPdf = () => {
    if (settingsLoading || isGeneratingPoster) return;

    generatePosterPdf();
  };

  return (
    <section className="w-full">
      

      {/* Offset pour la navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Aide & Conseils"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé + infos + action */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Un document d’aide mis à jour régulièrement
              </h2>

              <div className="mt-3 text-sm/6 max-w-3xl mx-auto text-center">
                <p>
                  Ce document regroupe des réponses aux{" "}
                  <span className="font-semibold">questions fréquentes</span>,
                  des{" "}
                  <span className="font-semibold">conseils personnalisés</span>{" "}
                  et des <span className="font-semibold">remarques</span>{" "}
                  adaptées à votre configuration.
                </p>

                <p className="mt-1 opacity-90">
                  Pensez à le consulter de temps en temps : il peut évoluer.
                </p>
              </div>

              <div className="mt-5 flex items-center justify-center">
                <Button
                  text="Ouvrir le document d’aide"
                  icon={FaExternalLinkAlt}
                  onClick={handleOpenDoc}
                  className="!rounded-xl !shadow-md gap-2"
                />
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {/* QR code vers le site public */}
            {siteUrl && (
              <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 p-5">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  QR Code vers votre site public
                </h3>

                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Imprimez-le et affichez-le dans vos salles pour faciliter
                  l’accès à votre site. Vous pouvez également créer une affiche
                  d’inscription prête à imprimer au format PDF.
                </p>

                <div className="mt-4 flex flex-col sm:flex-row items-center gap-4">
                  <div className="bg-white rounded-xl shadow p-4">
                    <QRCodeCanvas
                      ref={qrRef}
                      value={siteUrl}
                      size={300}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="H"
                      includeMargin
                    />
                  </div>

                  <div className="flex-1 w-full">
                    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/40 p-4">
                      <p className="text-sm text-slate-700 dark:text-slate-300">
                        URL cible :
                      </p>

                      <p className="mt-1 break-all text-sm font-medium text-slate-900 dark:text-slate-100">
                        {siteUrl}
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          text="Télécharger le QR Code"
                          icon={FaDownload}
                          onClick={handleDownloadQR}
                          className="!rounded-xl"
                        />

                        <Button
                          text={
                            isGeneratingPoster
                              ? "Création du PDF..."
                              : "Créer l’affiche PDF"
                          }
                          icon={isGeneratingPoster ? FaSpinner : FaFilePdf}
                          onClick={handleGeneratePosterPdf}
                          disabled={settingsLoading || isGeneratingPoster}
                          className={`!rounded-xl ${
                            isGeneratingPoster ? "animate-pulse" : ""
                          }`}
                        />

                        <a
                          href={siteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center rounded-xl px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          Ouvrir le site ↗
                        </a>
                      </div>

                      {registerUrl && (
                        <div className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:bg-blue-900/20 dark:text-blue-100">
                          L’affiche PDF pointera vers :{" "}
                          <span className="font-semibold break-all">
                            {registerUrl}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Aperçu du document */}
            <div className="mt-6">
              {docUrl ? (
                <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <iframe
                    title="Document d'aide"
                    src={
                      docUrl.includes("/edit")
                        ? docUrl.replace("/edit", "/preview")
                        : docUrl
                    }
                    className="w-full h-[75vh]"
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 text-sm dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-800">
                  Aucun document d’aide n’est configuré pour le moment.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
